import { useEffect, useState } from 'react';
import { CSVLink } from "react-csv";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useForm } from 'react-hook-form';
import { BiSolidDownArrow, BiSolidUpArrow } from 'react-icons/bi';
import { FiCalendar, FiDownload, FiSearch } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Checkbox from '../../../Components/Checkbox';
import InputBox from '../../../Components/Input';
import Loader from '../../../Components/Loader';
import Pagination from '../../../Components/Pagination';
import usePageFilter from '../../../hooks/usePageFilter';
import useUserData from '../../../hooks/useUserData';
import { Donation } from '../../../type/Donation';
import { API_ENDPOINTS } from '../../../utils/api';
import createAxiosInstance from "../../../utils/axiosInstance";
import { formatDateShort } from '../../../utils/formatters';
import CreateNewNeed from '../create-need';

const DonorsHome = () => {
  const me = useUserData();
  const axiosInstance = createAxiosInstance();

  const [recentDonorList, setRecentDonorList] = useState([]);
  const [cacheResult, setCacheResult] = useState<any[]>([]);
  const [rowPerPage, setRowPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoding, setIsLoding] = useState(true);
  const [defaultValues, setDefaultValues] = useState({
    search: "",
    startDate: null,
    endDate: null,
    activeDonor: []
  });
  // Sorting State
  const [sortColumn, setSortColumn] = useState<any>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const {
    register,
    watch,
    setValue,
    getValues
  } = useForm<{
    search: string;
    startDate: Date | null;
    endDate: Date | null;
    activeDonor: string[];
  }>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
  });

  const watchSearch = watch("search");
  const watchStartDate = watch("startDate");
  const watchEndDate = watch("endDate");
  const watchActiveDonor = watch("activeDonor") || [];

  useEffect(() => {
    filterResult();
  }, [watchSearch, watchStartDate, watchEndDate, watchActiveDonor]);

  const filterResult = () => {
    let result: any = cacheResult;
    if (getValues("search") !== "") {
      result = result.filter(
        (item: any) =>
          item.name.toLowerCase().includes(getValues("search").toLowerCase()) ||
          item.email.includes(getValues("search")) ||
          formatDateShort(item.createdAt).includes(getValues("search")) ||
          item.donations.toString().includes(getValues("search")) ||
          item.lastDonation.includes(getValues("search")) ||
          item.status.toLowerCase().includes(getValues("search").toLowerCase())
      );
    }
    const startDate = getValues("startDate");
    const endDate = getValues("endDate");
    if (startDate !== null && endDate !== null) {
      result = result.filter((item: any) => {
        const itemDate = new Date(formatDateShort(item.createdAt));
        return itemDate >= new Date(startDate) && itemDate <= new Date(endDate);
      });
    }
    if (getValues("activeDonor").includes("activeDonor")) {
      result = result.filter((item: any) => item.status === "Active");
    }
    setTotalPages(Math.ceil(result.length / rowPerPage));
    handleSort(sortColumn, result, sortOrder);
  };

  const handleSort = (column: keyof typeof recentDonorList[0], result: any = recentDonorList, passed_order?: "asc" | "desc") => {
    let order: any = passed_order || sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    if (passed_order) {
      order = passed_order;
    }
    setSortColumn(column);
    setSortOrder(order);

    // Sort Data
    const sortedData: any = [...result].sort((a, b) => {
      if (a[column] < b[column]) return order === "asc" ? -1 : 1;
      if (a[column] > b[column]) return order === "asc" ? 1 : -1;
      return 0;
    });

    setRecentDonorList(sortedData);
  };

  useEffect(() => {
    register("startDate");
    register("endDate");
    getDonersList();
  }, [me]);

  const headers = [
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Date Connected", key: "createdAt" },
    { label: "Donations #", key: "donations" },
    { label: "Last Donation", key: "lastDonation" },
    { label: "Status", key: "status" }
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getDonersList = async () => {
    try {
      const donationData = await axiosInstance.get(`${API_ENDPOINTS.SAFENIIGHT.GETRECENTDONATIONS}?expand=need/assessments,donor`);
      const donations = donationData.data;

      const donorsData = await axiosInstance.get(API_ENDPOINTS.SAFENIIGHT.GETDONERS);
      const donors = donorsData.data;

      const donorsWithDonations =
        donors.map((donor: any) => ({
          ...donor,
          donations: donations.filter(
            (donation: any) => donor.donorId === donation.donorId,
          ),
        }));

      donations
        .reduce(
          (
            previousValue: { email: string; donations: Donation[] }[],
            currentValue: Donation,
          ) => {
            if (currentValue.donor) return previousValue;

            const email = previousValue.find(
              ({ email }) => email === currentValue.email,
            );

            if (email) email.donations.push(currentValue);
            else
              previousValue.push({
                email: currentValue.email,
                donations: [currentValue],
              });

            return previousValue;
          },
          [] as { email: string; donations: Donation[] }[],
        )
        .forEach(({ email, donations }: { email: string; donations: Donation[] }) => {
          const [{ firstName, lastName }] = donations;

          donorsWithDonations.push({
            email,
            donations: [],
            active: false,
            firstName,
            lastName,
          });
        });

      const data: any = [];

      donorsWithDonations.map((element: any) => {
        const obj = {
          name: `${element.firstName} ${element.lastName}`,
          email: element.email,
          createdAt: element.createdAt,
          donations: element.donations?.length > 0 ? element.donations?.length : 0,
          lastDonation: element.donations?.length
            ? formatDateShort(Math.max(
              ...element.donations.map((donation: any) =>
                new Date(donation.donationDate).getTime(),
              ),
            )
            ) : "",
          status: element.blocked?.includes(me?.agencyId)
            ? "Blocked"
            : element.active
              ? "Active"
              : "Inactive",
          referenceUrl: {
            name: `/safenight/donors/donor-detail/${element.donorId}`,
            email: element.email ? `mailto:${element.email}` : null,
          }
        };
        data.push(obj);
      });

      setTotalPages(Math.ceil(data.length / rowPerPage));
      handleSort("name", data, "asc");
      setCacheResult(data);
      setValue("activeDonor", ["activeDonor"]);
      setIsLoding(false);
    } catch (error) {
      setIsLoding(false);
      console.log(error);
    }
  };

  const updateStartDate = (date: any) => {
    setDefaultValues((prev) => ({ ...prev, startDate: date }));
    setValue("startDate", date);
  };

  const updateEndDate = (date: any) => {
    setDefaultValues((prev) => ({ ...prev, endDate: date }));
    setValue("endDate", date);
  };

  const filteredRequests = usePageFilter(
    recentDonorList,
    (currentPage - 1) * rowPerPage,
    rowPerPage
  );

  return (
    <>
      {isLoding && <Loader />}
      <div className="container py-4 md:py-10 2xl:py-[70px]">
        <div className="mb-3 md:mb-4 2xl:mb-8 flex justify-center">
          <CreateNewNeed />
        </div>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-gray-900 font-semibold text-xl">Donors</h3>
          {(recentDonorList && recentDonorList.length > 0) && (
            <CSVLink filename={`donors.csv`} data={recentDonorList} headers={headers}>
              <button className='btn btn-outline-gray py-2 2xl:py-3'>
                <FiDownload size={20} />
                Export to Excel
              </button>
            </CSVLink>
          )}
        </div>
        <div className="my-3 flex flex-wrap -mx-3">
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <label htmlFor="whatFees" className='block text-md font-medium text-gray-500 text-end'> From :</label>
            <div className="flex-1 relative">
              <DatePicker selected={defaultValues.startDate} onChange={(date: Date | null) => updateStartDate(date)} calendarClassName="ssc-custom-calendar" className='form-control w-full' />
              <FiCalendar size={20} className='text-gray-600 absolute top-1/2 -translate-y-1/2 right-3' />
            </div>
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <label htmlFor="whatFees" className='block text-md font-medium text-gray-500 text-end'> To :</label>
            <div className="flex-1 relative">
              <DatePicker selected={defaultValues.endDate} onChange={(date: Date | null) => updateEndDate(date)} calendarClassName="ssc-custom-calendar" className='form-control w-full' />
              <FiCalendar size={20} className='text-gray-600 absolute top-1/2 -translate-y-1/2 right-3' />
            </div>
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <label htmlFor="whatFees" className='block text-md font-medium text-gray-500 text-end'> To :</label>
            <div className="flex-1">
              <InputBox
                type="search"
                placeholder="Search"
                iconRight={<FiSearch />}
                {...register('search')}
              />
            </div>
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <div className="card py-2.5 rounded-lg px-4 shadow-none">
              <Checkbox
                label='Show Active Donors Only'
                selectedValue={watchActiveDonor}
                value='activeDonor'
                checkboxClass='h-fit flex items-center'
                {...register("activeDonor")}
              />
            </div>
          </div>
        </div>
        <div className="relative md:overflow-hidden md:border md:border-borderGray rounded-xl">
          <table className="table_responsive_block w-full text-xs 2xl:text-sm text-left rtl:text-right">
            <thead className="text-xs 2xl:text-sm text-gray-600 bg-gray-100 border-b">
              <tr>
                {headers.map((col, index) => (
                  <th
                    key={index}
                    onClick={() => handleSort(col.key as keyof typeof recentDonorList[0])}
                    className="px-4 2xl:px-6 py-3 font-medium cursor-pointer"
                  >
                    {col.label}
                    {sortColumn === col.key && (sortOrder === 'desc' ? <BiSolidDownArrow className="inline text-xs ml-1" /> : <BiSolidUpArrow className="inline text-xs ml-1" />)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((donor: any, index: number) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td data-title='Name' className="px-4 2xl:px-6 py-3 text-primary"><Link to={donor.referenceUrl.name}>{donor.name}</Link></td>
                  <td data-title='Email' className="px-4 2xl:px-6 py-3 text-primary"><Link to={donor.referenceUrl.email}><span className='truncate'>{donor.email}</span></Link></td>
                  <td data-title='Date Connected' className="px-4 2xl:px-6 py-3">{formatDateShort(donor.createdAt)}</td>
                  <td data-title='Donation #' className="px-4 2xl:px-6 py-3">{donor.donations}</td>
                  <td data-title='Last Donation' className="px-4 2xl:px-6 py-3">{donor.lastDonation}</td>
                  <td data-title='Status' className="px-4 2xl:px-6 py-3">
                    <span className={`badge ${donor.status === "Active" ? "badge-success" : "badge-danger"}`}>
                      {donor.status}
                    </span>
                  </td>
                </tr>
              ))}
              {filteredRequests.length === 0 && (
                <tr>
                  <td colSpan={headers.length} className="text-center py-4 text-gray-500">There are no donors.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-5 flex justify-between items-center">
          <div></div>
          <Pagination totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      </div>
    </>
  );
};

export default DonorsHome;
